/**
 * Application configuration for different environments
 * Handles both local development and production deployment scenarios
 */

export interface AppConfig {
  auth: {
    clientId: string;
    authority: string;
    tenantId: string;
    redirectUri: string;
    requiredAppRole: string;
    apiScope: string;
  };
  api: {
    baseUrl: string;
  };
  environment: 'development' | 'production';
}

/**
 * Unified configuration that works for both development and production
 * Uses Vite environment variables that are provided by Azure App Service during build
 */
const appConfig: AppConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: import.meta.env.VITE_AZURE_AUTHORITY || 'https://login.microsoftonline.com/common',
    tenantId: import.meta.env.VITE_AZURE_TENANT_ID || '',
    redirectUri: window.location.origin,
    requiredAppRole: import.meta.env.VITE_REQUIRED_APP_ROLE || 'MedMgmtAuthAiUser_d',
    apiScope: import.meta.env.VITE_AZURE_API_SCOPE || 'api://MedMgmtAuthAI-api.dev/User.MedMgmtAuthAi.API',
  },
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  },
  environment: import.meta.env.PROD ? 'production' : 'development',
};

/**
 * Get the current configuration
 */
export function getAppConfig(): AppConfig {
  // Validate that required config values are present
  if (!appConfig.auth.clientId || appConfig.auth.clientId.startsWith('#{')) {
    console.warn('Azure AD Client ID not configured. Authentication will not work.');
  }
  
  return appConfig;
}

/**
 * Check if authentication is properly configured
 */
export function isAuthConfigured(): boolean {
  return !!(
    appConfig.auth.clientId && 
    !appConfig.auth.clientId.startsWith('#{') && 
    appConfig.auth.authority && 
    !appConfig.auth.authority.startsWith('#{')
  );
}

export default getAppConfig;
